/**
 * UserMenu — avatar do usuário logado com dropdown "Meus dados" / "Sair".
 * TH-55256 / subtask de TH-55253 (área do usuário — topbar).
 *
 * Acessa o usuário atual via `useAuth()` (GET /api/auth/me já cacheado).
 * Logout delega a `useLogout()` — sem duplicar lógica de cookie/redirect.
 * A página /meus-dados é criada em TH-55255; o link já pode apontar pra lá.
 *
 * Avatar: se `user.avatar_url` não existir no model atual, renderiza iniciais
 * (até 2 chars) com background determinístico baseado em hash do email/nome.
 * Usa tokens CSS do DS — sem cores hex hardcoded.
 *
 * i18n: usa useTranslation() + LanguageSwitcher (PSHWO-56859).
 */

import { Link } from "@tanstack/react-router";
import { LogOut, User } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { useLogout } from "@/hooks/useLogout";

/** Gera iniciais (máx 2 chars) a partir do nome ou email. */
function getInitials(name: string | null, email: string): string {
  const source = name?.trim() || email;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Background determinístico: hash simples do string → hue HSL.
 * Retorna uma string CSS `hsl(H deg 45% 30%)` para dark theme.
 */
function avatarBgColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return `hsl(${hue}deg 45% 30%)`;
}

export function UserMenu() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logout = useLogout();

  const displayName = user?.name?.trim() || user?.email || t("user.profile");
  const email = user?.email || "";
  const initials = user ? getInitials(user.name, user.email) : "?";
  const bgColor = user ? avatarBgColor(user.email) : "var(--color-muted)";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("user.menu")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold text-white ring-2 ring-transparent transition-all hover:ring-[var(--color-border)] focus-visible:outline-none focus-visible:ring-[var(--color-primary)]"
          style={{ background: bgColor }}
          data-testid="user-menu-trigger"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" data-testid="user-menu-content">
        {/* Cabeçalho não-clicável: nome + email */}
        <div className="px-3 py-2" data-testid="user-menu-header">
          <div className="truncate text-[13px] font-semibold text-[var(--color-foreground)]">
            {displayName}
          </div>
          {email && email !== displayName ? (
            <div className="truncate font-mono text-[11px] text-[var(--color-muted-foreground)]">
              {email}
            </div>
          ) : null}
        </div>

        <DropdownMenuSeparator />

        {/* Meus dados — link para /meus-dados (TH-55255) */}
        <DropdownMenuItem asChild>
          <Link
            to="/meus-dados"
            data-testid="user-menu-my-data"
            className="flex w-full items-center gap-2"
          >
            <User className="h-4 w-4 shrink-0 text-[var(--color-muted-foreground)]" />
            {t("user.myData")}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Seletor de idioma (PSHWO-56859) */}
        <LanguageSwitcher />

        <DropdownMenuSeparator />

        {/* Sair */}
        <DropdownMenuItem
          onSelect={() => logout.mutate()}
          data-testid="user-menu-logout"
          disabled={logout.isPending}
          className="text-[var(--color-destructive)] focus:text-[var(--color-destructive)]"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {logout.isPending ? t("auth.loggingOut") : t("auth.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
